import { inject, observer } from 'mobx-react';
import React, { ChangeEventHandler, MouseEventHandler } from 'react';
import styled from 'styled-components';

import { NotificationStore } from '../../stores';
import { authStore } from '../../stores/authStore';

import { Attachement, Importancia, Notification } from '../../models';

import { DateTimeUtils } from '@microsoft/applicationinsights-common';
import {
    Alert,
    Badge,
    Button,
    Checkbox,
    Col,
    DatePicker,
    Icon,
    Input,
    Popconfirm,
    Radio,
    Row,
    Select,
    Steps,
    Upload,
    Tooltip,
    notification
} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { RangePickerValue } from 'antd/lib/date-picker/interface';
import { RadioChangeEvent } from 'antd/lib/radio/interface';
import { HttpRequestHeader, UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import moment from 'moment';
import TokenStore from '../../stores/tokenStore';

interface NotificationCardProps {
    notificationStore?: NotificationStore;
    notification?: Notification;
    kind: 'preview' | 'full';
    editable: boolean;
    tokenStore?: TokenStore;
}

const StyledCardNotification = styled(Alert)`
    margin: 25px auto;
    position: relative;
`;

const StyledCardNotificationPreview = styled(StyledCardNotification)`
    cursor: pointer;
    white-space: 'normal';
`;

const SmallerUploadCard = styled(Upload)`
    .ant-upload-list-picture-card .ant-upload-list-item {
        width: 75px;
        height: 75px;
    }
`;

const XLargeInput = styled(Input)`
    height: 48px !important;
    padding: 10px 14px !important;
    font-size: 20px !important;
    margin-bottom: 18px !important;
`;

const PaddedRow = styled(Row)`
    margin: 36px 0px;
`;

const ActionRow = styled(Row)`
    margin-top: 36px;
`;

const DescriptionArea = styled(Input.TextArea)`
    margin-bottom: 36px !important;
`;

const BottomRight = styled.p`
    position: absolute;
    bottom: 0;
    right: 15px;
`;

const BottomCenter = styled.p`
    position: absolute;
    bottom: 0;
    left: 220px;
`;

const BottomLeft = styled.div`
    position: absolute;
    bottom: 0;
    margin-bottom: 1em;
    left: 50px;
`;

const BottomLeftIcon = styled.div`
    position: absolute;
    bottom: 0;
    margin-bottom: 1em;
`;

const BottomArchivar = styled.p`
    position: absolute;
    bottom: 0;
    border:none;
    margin-bottom: 1em;
`;
const BiggerIcon = styled(Icon)`
    font-size: 20px;
`;

const RightDiv = styled.div`
    float: right;
`;

@inject('notificationStore')
@inject('tokenStore')
@observer
export class NotificationCard extends React.Component<NotificationCardProps> {
    getHeaders = () => {
        const { tokenStore } = this.props;
        const headers: HttpRequestHeader = {
            Authorization: `Bearer ${tokenStore!.token}`
        };
        return headers;
    };
    steps = () => [
        {
            title: 'Descripción',
            content: (
                <>
                    <DescriptionArea
                        placeholder="Descripción de la notificación"
                        autosize={{ minRows: 2, maxRows: 5 }}
                        onChange={this.onDescriptionChange}
                        maxLength={250}
                    />
                    <RightDiv>
                        Vigencia{' '}
                        {
                            <DatePicker.RangePicker
                                disabledDate={this.validateDate}
                                defaultValue={[moment(), moment()]}
                                format="YYYY-MM-DD"
                                onChange={this.onCalendarRangeChange}
                                allowClear={false}
                            />
                        }
                    </RightDiv>
                </>
            )
        },
        {
            title: 'Adjuntos',
            content: (
                <>
                    <Col span={12}>
                        <Input
                            placeholder="Descripción del archivo"
                            value={this.props.notificationStore!.notificationDescription}
                            onChange={this.onNotificationDescriptionChange}
                            maxLength={50}
                        />
                    </Col>
                    <Upload
                        listType="picture"
                        headers={this.getHeaders()}
                        action={`${process.env.REACT_APP_NOTIFICACIONES_ENDPOINT}/notificaciones/${
                            this.props.notificationStore!.selectedNotification.id
                        }/archivos`}
                        accept=".doc,.docx,.pdf"
                        onChange={this.onChange}
                        onRemove={this.onRemove}
                        data={{
                            descripcion: this.props.notificationStore!.notificationDescription
                        }}
                    >
                        <Col span={12}>
                            <Button
                                disabled={this.props.notificationStore!.noAttachementDescription}
                            >
                                <Icon type="upload" />
                                Subir archivo
                            </Button>
                        </Col>
                    </Upload>
                </>
            )
        },
        {
            title: 'Audiencia',
            content: (
                <Row gutter={16}>
                    <Col span={12}>
                        <Checkbox onChange={this.onAllMunicipalitiesSelected}>
                            Todos Municipios
                        </Checkbox>
                        <Select
                            mode="multiple"
                            placeholder="Municipios seleccionados"
                            onChange={this.onMunicipalitiesSelected}
                            value={this.props.notificationStore!.selectedMunicipalities}
                            style={{ width: '100%', margin: '18px 0' }}
                            disabled={this.props.notificationStore!.selectedAllMunicipalities}
                            maxTagCount={10}
                        >
                            {this.props.notificationStore!.municipalities.map(municipio => (
                                <Select.Option key={municipio}>{municipio}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={12}>
                        <Checkbox onChange={this.onAllOrganismosSelected}>
                            Todos Organismos
                        </Checkbox>
                        <Select
                            mode="multiple"
                            placeholder="Organismos Operadores seleccionados"
                            onChange={this.onOrganismosSelected}
                            value={this.props.notificationStore!.selectedOrganismos}
                            style={{ width: '100%', margin: '18px 0' }}
                            disabled={this.props.notificationStore!.selectedAllOrganismos}
                            maxTagCount={10}
                        >
                            {this.props.notificationStore!.organismos.map(organismo => (
                                <Select.Option key={organismo}>{organismo}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
            )
        }
    ];

    validateDate = (current: any) => {
        return current && current.valueOf() < moment().subtract(1, 'day');
    };

    onChange = (info: UploadChangeParam) => {
        if (info.file.status === 'done') {
            this.onUpload(info.file);
        }
    };

    onAllOrganismosSelected = (e: CheckboxChangeEvent) => {
        const { notificationStore } = this.props;
        notificationStore!.editAllOrganismos(e.target.checked);
    };

    onAllMunicipalitiesSelected = (e: CheckboxChangeEvent) => {
        const { notificationStore } = this.props;
        notificationStore!.editAllMunicipalities(e.target.checked);
    };

    onOrganismosSelected = (value: string[]) => {
        const { notificationStore } = this.props;
        notificationStore!.editAudience(value);
    };

    onMunicipalitiesSelected = (value: string[]) => {
        const { notificationStore } = this.props;
        notificationStore!.editMunicipalities(value);
    };

    onNotificationDescriptionChange: ChangeEventHandler<HTMLInputElement> = event => {
        const { notificationStore } = this.props;
        notificationStore!.editNotificationDescription(event.target.value);
    };

    onUpload = (file: UploadFile) => {
        const { notificationStore } = this.props;
        notificationStore!.addAttachements(file);
        return true;
    };

    onCalendarRangeChange = (dates: RangePickerValue, dateStrings: [string, string]) => {
        const { notificationStore } = this.props;
        notificationStore!.editDates(dateStrings[0], dateStrings[1]);
    };

    handleCancel = () => {
        const { notificationStore } = this.props;
        notificationStore!.unselectNotification();
    };

    onClick: MouseEventHandler<HTMLDivElement> = () => {
        const { notification, notificationStore } = this.props;
        notificationStore!.selectNotification(notification!);
    };

    onPreview = async (file: UploadFile) => {
        const response = await this.props.notificationStore!.getAttachement(file as Attachement);

        if (!response) {
            return;
        }

        const type = (file as Attachement).contentTypeHeader;
        const blob = new Blob([response!.data], { type });
        const url = window.URL.createObjectURL(blob);

        if (type === 'application/pdf') {
            const newWindow = window.open('/');
            newWindow!.onload = () => {
                newWindow!.location.replace(url);
            };
        } else {
            const link = document.createElement('a');
            link.href = url;
            link.click();
            window.URL.revokeObjectURL(url);
        }
    };

    onRemove = (file: UploadFile) => {
        const { notificationStore } = this.props;
        if (file.response.nombre) {
            notificationStore!.deleteAttachement(file.response.nombre);
        }
        notificationStore!.removeAttachement(file);
    };

    renderAttachements = (archivosAdjuntos: Attachement[]) => {
        const { kind } = this.props;
        return kind === 'full' ? (
            <SmallerUploadCard
                fileList={archivosAdjuntos.map((attachement, i) => {
                    attachement.name = attachement.descripcion;
                    attachement.uid = i.toString();
                    attachement.status = 'done';
                    attachement.url = '.txt';
                    return attachement;
                })}
                showUploadList={{ showRemoveIcon: false }}
                listType="picture"
                onPreview={this.onPreview}
            />
        ) : (
            <BottomLeftIcon>
                <Badge count={archivosAdjuntos.length}>
                    <BiggerIcon type="copy" />
                </Badge>
            </BottomLeftIcon>
        );
    };

    renderReadonlyNotification = () => {
        const { kind, notification, notificationStore } = this.props;
        const { canEdit } = notificationStore!;
        const {
            titulo,
            descripcion,
            archivosAdjuntos,
            fechaInicio,
            fechaFin,
            importancia
        } = notification!;
        const Card = kind === 'full' ? StyledCardNotification : StyledCardNotificationPreview;
        const onClick = kind === 'preview' ? this.onClick : undefined;
        const alertType =
            importancia === Importancia.Alto
                ? 'error'
                : importancia === Importancia.Medio
                ? 'warning'
                : 'info';
        const popConfirmDelete = '¿Estás seguro de que quieres eliminar la notificación?';
        const popConfirmArchivar = '¿Estás seguro de que quieres archivar la notificación?';
        const stylePrimary = {
            backgroundColor:'#a8123e', borderColor:'#a8123e'
        };
        const styleVeda = {
            backgroundColor:'#3c3c3b', borderColor:'#3c3c3b'
        };
        return (
            <div onClick={onClick}>
                <Card
                    message={titulo}
                    description={
                        <div>
                            <p>{descripcion}</p>
                            {archivosAdjuntos.length > 0 &&
                                this.renderAttachements(archivosAdjuntos)}
                            <br />
                            <br />
                            {canEdit && kind === 'full' && (
                                <>
                                    <BottomLeft>
                                        <Popconfirm
                                            title={popConfirmDelete}
                                            onConfirm={this.handleDelete}
                                            placement="bottom"
                                            cancelText="Cancelar"
                                            okText="Aceptar"
                                        >
                                            <Button size="small" type="danger">
                                                Borrar
                                            </Button>
                                        </Popconfirm>
                                    </BottomLeft>

                                    <Tooltip placement='top' title='Archivar Notificación'>
                                    <BottomArchivar>
                                        <Popconfirm
                                            title={popConfirmArchivar}
                                            onConfirm={this.handleArchivar}
                                            placement="bottom"
                                            cancelText="Cancelar"
                                            okText="Aceptar"
                                        >
                                            <Button size="small" type="default"  >
                                            <Icon type="paper-clip"   />
                                            </Button>
                                        </Popconfirm>
                                    </BottomArchivar>
                                    </Tooltip>

                                    <BottomCenter style={{ left: '110px' }}>
                                        <Button
                                            size="small"
                                            type="primary"
                                            onClick={this.handleReporte}
                                            style={styleVeda}
                                        >
                                            Reporte
                                        </Button>
                                    </BottomCenter>
                                </>
                            )}
                            <br />
                            <BottomRight>
                                Vigencia: {fechaInicio.substring(0, 10)} -{' '}
                                {fechaFin.substring(0, 10)}
                            </BottomRight>
                        </div>
                    }
                    type={alertType}
                />
            </div>
        );
    };

    handleReporte = async () => {
        const { notificationStore } = this.props;
        const response = await notificationStore!.getReporteNotificacion();
        if (response !== undefined) {
            const type = 'application/pdf';
            const blob = new Blob([response!.data], { type });
            const url = window.URL.createObjectURL(blob);

            const newWindow = window.open('/');
            newWindow!.onload = () => {
                newWindow!.location.replace(url);
            };
        }
    };

    handleDelete = async () => {
        const { notificationStore, notification } = this.props;
        await notificationStore!.deleteNotification(notification!.id);
        notificationStore!.reset();
    };
    handleArchivar = async () => {
        const { notificationStore, notification } = this.props;
        await notificationStore!.ArchivarNotificacion(notification!.id);
        notificationStore!.reset();
    };

    nextStep = async () => {
        const { notificationStore } = this.props;
        const { currentStep } = notificationStore!;
        if (currentStep < this.steps().length - 1) {
            await notificationStore!.nextStep();
        } else {
            await notificationStore!.done();
        }
    };

    cancel = async () => {
        const { notificationStore } = this.props;
        const { id } = notificationStore!.selectedNotification;
        if (id > 0) {
            notificationStore!.deleteNotification(id);
        }
        notificationStore!.reset();
    };

    onImportanciaChange = (e: RadioChangeEvent) => {
        const { notificationStore } = this.props;
        notificationStore!.editImportancia(e.target.value);
    };

    onTitleChange: ChangeEventHandler<HTMLInputElement> = event => {
        const { notificationStore } = this.props;
        notificationStore!.editTitle(event.target.value);
    };

    onDescriptionChange: ChangeEventHandler<HTMLTextAreaElement> = event => {
        const { notificationStore } = this.props;
        notificationStore!.editDescription(event.target.value);
    };

    renderEditableNotification = () => {
        const { notificationStore } = this.props;
        const { notificationType, currentStep, canMoveNextStep, isFetching } = notificationStore!;
        const nextStepName = currentStep === 2 ? 'Aceptar' : 'Siguiente';
        const stylePrimary = {
            backgroundColor:'#a8123e', borderColor:'#a8123e', color:'#fff'
        };
        const styleVeda = {
            backgroundColor:'#3c3c3b', borderColor:'#3c3c3b', color:'#fff'
        };
        return (
            <StyledCardNotification
                message={
                    <>
                        <XLargeInput
                            placeholder="Título"
                            onChange={this.onTitleChange}
                            maxLength={100}
                        />
                        <Radio.Group
                            buttonStyle="solid"
                            options={[Importancia.Alto, Importancia.Medio, Importancia.Bajo]}
                            defaultValue={Importancia.Bajo}
                            onChange={this.onImportanciaChange}
                        />
                    </>
                }
                description={
                    <>
                        <PaddedRow>
                            <Steps current={currentStep}>
                                {this.steps().map(item => (
                                    <Steps.Step key={item.title} title={item.title} />
                                ))}
                            </Steps>
                        </PaddedRow>
                        <PaddedRow className="steps-content">
                            {this.steps()[currentStep].content}
                        </PaddedRow>
                        <ActionRow className="steps-action">
                            {
                                <Button type="danger" onClick={this.cancel}>
                                    Cancelar
                                </Button>
                            }
                            {currentStep < this.steps().length && (
                                <RightDiv>
                                    <Button
                                        type="primary"
                                        onClick={this.nextStep}
                                        disabled={!canMoveNextStep}
                                        loading={isFetching}
                                        style={styleVeda}
                                    >
                                        {nextStepName}
                                    </Button>
                                </RightDiv>
                            )}
                        </ActionRow>
                    </>
                }
                type={notificationType}
            />
        );
    };

    render() {
        const { editable } = this.props;
        return editable ? this.renderEditableNotification() : this.renderReadonlyNotification();
    }
}
